import { useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp, updateDoc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { orgQuery, withOrg } from "@/lib/org";
import { parseAddressEmail, matchStudent } from "@/lib/addressParser";
import { deriveAddressFields, normalizeBungil } from "@/lib/excelParse";
import { geocodeAddress } from "@/lib/vworld";

import { plainError } from "@/lib/plainError";
export default function AddressUpdates() {
  const [body, setBody] = useState("");
  const [parsed, setParsed] = useState<ReturnType<typeof parseAddressEmail> | null>(null);
  const [matches, setMatches] = useState<{ id: string; name: string; school: string; refinedAddress: string; score: number }[]>([]);
  const [status, setStatus] = useState("");
  const [selectedId, setSelectedId] = useState("");
  // 실제로 적용할 새 주소 — 읽어낸 값으로 채우고 직원이 고칠 수 있다
  const [newAddr, setNewAddr] = useState("");

  // 시·구가 빠진 주소는 그대로 둔다 — 지도 찾기 서버가 업체 담당 시·도를 붙여 찾는다.
  // (학생의 옛 구를 붙이면 다른 구로 이사한 집이 틀린 구로 검색된다 — 4차 QA 15-4)
  const pickStudent = (id: string, p = parsed) => {
    setSelectedId(id);
    setNewAddr((p?.newAddress || "").trim());
  };

  const analyze = async () => {
    setStatus("분석 중…");
    const p = parseAddressEmail(body);
    setParsed(p);

    // 라벨 파싱에 의존하지 않고 전체 명단을 본문과 직접 대조 (학교 표기 차이로 0건 나던 문제 해결)
    const snap = await getDocs(orgQuery("students"));
    const candidates = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
    const m = matchStudent(
      body,
      candidates.map((c) => ({ id: c.id, name: c.name, school: c.school, phone: c.phone, guardian: c.guardian })),
    );
    const merged = m
      .map((x) => {
        const c = candidates.find((c) => c.id === x.id)!;
        return { id: c.id, name: c.name, school: c.school, refinedAddress: c.refinedAddress, score: x.score };
      })
      .slice(0, 5);
    setMatches(merged);
    pickStudent(merged[0]?.id || "", p);
    const kind = p.intent === "delete" ? "전출/삭제" : "주소변경";
    if (merged.length === 0) {
      setStatus(`후보 0건 — 본문에서 학생 이름이나 연락처를 찾지 못했습니다. 이름/연락처를 포함해 다시 입력해보세요. (의도: ${kind})`);
    } else {
      setStatus(`읽기 완료 (${kind}, ${p.confidence === "high" ? "확실" : p.confidence === "medium" ? "보통" : "불확실"}) — 후보 ${merged.length}건`);
    }
  };

  const apply = async () => {
    if (!parsed || !selectedId || !newAddr.trim()) return;
    setStatus("적용 중…");
    try {
      // 번길 띄어쓰기 정규화 — 어긋난 표기는 지오코더가 다른 길로 스냅한다(함정 #8)
      const newAddress = normalizeBungil(newAddr.replace(/\s+/g, " ").trim());
      // 1) 지도 위치 찾기 — 못 찾으면 옛 위치를 남기지 않고 '확인 필요'로 표시한다(안 그러면 길안내가 이사 전 집으로 간다)
      let geo: Record<string, unknown> = { lat: null, lng: null, geocodeStatus: "needs_review" };
      let found = false;
      let refined = newAddress;
      try {
        const p = await geocodeAddress(newAddress);
        if (p?.roadMismatch) {
          const goOn = window.confirm(
            `⚠️ 지도 검색이 입력 주소와 다른 길로 매칭했습니다.\n\n입력: ${newAddress}\n매칭: ${p.matched}\n\n주소 표기(번길 숫자·띄어쓰기)를 다시 확인하세요. 그래도 이대로 적용할까요?`,
          );
          if (!goOn) {
            setStatus("⏸ 적용 취소 — 주소를 확인한 뒤 다시 시도하세요.");
            return;
          }
        }
        if (p) {
          found = true;
          refined = p.matched || newAddress; // 표준 표기 "(동 이름,단지명)"에서 동 이름이 나온다
          geo = { lat: p.y, lng: p.x, geocodeStatus: "ok", ...(p.gu ? { gu: p.gu } : {}), ...(p.dong ? { dong: p.dong } : {}) };
        }
      } catch (e) {
        console.warn("주소 변경 지오코딩 실패", e);
      }

      await updateDoc(doc(db, "students", selectedId), {
        // 새 주소 기준으로 구·동·건물·호(층)를 재계산 — 이전 주소의 동·호가 남는 것 방지
        ...deriveAddressFields(refined, newAddress),
        refinedAddress: refined,
        rawAddress: newAddress,
        ...(parsed.phone ? { phone: parsed.phone } : {}),
        ...geo,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "addressUpdates"), withOrg({
        emailBody: body,
        parsed,
        appliedAddress: newAddress,
        matchedStudentId: selectedId,
        applied: true,
        appliedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      }));

      setStatus(found ? "✅ 적용 완료 — 지도 위치까지 바뀌었습니다." : "⚠️ 주소는 바꿨지만 지도에서 위치를 못 찾았습니다. 「주소 확인 대기」에서 위치를 잡아 주세요. 그 전에는 기사 경로에서 빠집니다.");
      setBody("");
      setParsed(null);
      setMatches([]);
      setNewAddr("");
    } catch (e: any) {
      setStatus(`❌ 오류: ${plainError(e)}`);
    }
  };

  // 전출/전학 학생 삭제 — 선택한 학생 1명을 명단에서 제거하고 삭제 로그를 남긴다.
  const deleteStudent = async () => {
    const target = matches.find((m) => m.id === selectedId);
    if (!target) return;
    if (!window.confirm(`${target.school} ${target.name} 학생을 명단에서 삭제합니다. 되돌릴 수 없습니다. 계속할까요?`)) return;
    setStatus("삭제 중…");
    try {
      // 문서를 통째로 보관해 학생 관리 「전출 학생 되살리기」에서 되돌릴 수 있게 한다
      const before = (await getDoc(doc(db, "students", target.id))).data() || null;
      await addDoc(collection(db, "studentDeletions"), withOrg({
        studentId: target.id,
        name: target.name,
        school: target.school,
        refinedAddress: target.refinedAddress || "",
        reason: body,
        deletedAt: serverTimestamp(),
        before,
      }));
      await deleteDoc(doc(db, "students", target.id));
      setStatus(`✅ ${target.school} ${target.name} 전출 처리(삭제) 완료`);
      setBody("");
      setParsed(null);
      setMatches([]);
      setSelectedId("");
    } catch (e: any) {
      setStatus(`❌ 오류: ${plainError(e)}`);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">✉️ 주소 변경 · 전출 처리</h1>
      <p className="text-sm text-slate-500">
        학부모 메시지를 붙여넣으면 명단과 대조해 학생을 찾아줍니다. 새 주소가 있으면 <strong>주소 변경</strong>, 전출/전학 안내면 <strong>전출 처리(삭제)</strong>로 자동 분기합니다. 후보를 선택하고 확인 후 진행하세요.
      </p>

      <div className="card p-4 space-y-3">
        <textarea
          className="input min-h-[180px]"
          placeholder="[주소변경] ○○중 김하나 학생 보호자입니다. 이사해서 주소가 바뀌었어요.&#10;새 주소: (시·도부터 동·호수까지)&#10;연락처 010-1234-5678&#10;&#10;[전출] 구덕고 한석민 학생 이번에 전학 갔어요. 명단에서 빼주세요."
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex gap-2">
          <button className="btn-primary" onClick={analyze} disabled={!body.trim()}>AI 분석</button>
          <button className="btn-ghost" onClick={() => { setBody(""); setParsed(null); setMatches([]); setStatus(""); setNewAddr(""); }}>초기화</button>
        </div>
      </div>

      {parsed && (
        <div className="card p-4 text-sm">
          <h3 className="font-bold mb-2">
            읽어낸 내용
            <span className={`chip ml-2 ${parsed.intent === "delete" ? "bg-rose-100 text-rose-700" : "bg-milk-100 text-milk-700"}`}>{parsed.intent === "delete" ? "전출/삭제" : "주소변경"}</span>
            <span className={`chip ml-2 ${parsed.confidence === "high" ? "bg-emerald-100 text-emerald-700" : parsed.confidence === "medium" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{parsed.confidence === "high" ? "확실" : parsed.confidence === "medium" ? "보통" : "불확실"}</span>
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <Field label="학생" value={parsed.studentName} />
            <Field label="학교" value={parsed.school} />
            <Field label="보호자" value={parsed.guardian} />
            <Field label="연락처" value={parsed.phone} />
            <Field label="기존 주소" value={parsed.oldAddress} className="col-span-2" />
            <Field label="새 주소" value={parsed.newAddress} className="col-span-2" />
          </div>
          <div className="text-xs text-slate-400 mt-2">{parsed.reason}</div>
        </div>
      )}

      {matches.length > 0 && (
        <div className="card p-4">
          <h3 className="font-bold mb-2">매칭 후보 학생 (점수순)</h3>
          <div className="space-y-2 text-sm">
            {matches.map((m) => (
              <label key={m.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer">
                <input type="radio" name="match" value={m.id} checked={selectedId === m.id} onChange={() => pickStudent(m.id)} />
                <div className="flex-1">
                  <div className="font-medium">{m.school} {m.name}</div>
                  <div className="text-xs text-slate-500">{m.refinedAddress}</div>
                </div>
                <div className="chip bg-milk-100 text-milk-700">{m.score}점</div>
              </label>
            ))}
          </div>
          {parsed?.intent === "delete" ? (
            <div className="mt-4 space-y-1">
              <button
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-rose-600 text-white disabled:opacity-40"
                onClick={deleteStudent}
                disabled={!selectedId}
              >
                선택한 학생 전출 처리 (삭제)
              </button>
              <p className="text-xs text-rose-600">전출/전학 요청으로 판단했습니다. 삭제는 되돌릴 수 없습니다.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <label className="block">
                <span className="label">적용할 새 주소 (확인하고 필요하면 고치세요)</span>
                <input
                  className="input"
                  value={newAddr}
                  onChange={(e) => setNewAddr(e.target.value)}
                  placeholder="예: 부산광역시 해운대구 해운대로 468, 101동 1404호"
                />
              </label>
              {!newAddr.trim() && (
                <p className="text-xs text-rose-600">문자에서 새 주소를 찾지 못했습니다. 위 칸에 새 주소를 직접 넣으면 버튼이 켜집니다.</p>
              )}
              <button className="btn-primary" onClick={apply} disabled={!selectedId || !newAddr.trim()}>
                선택한 학생에 새 주소 적용
              </button>
            </div>
          )}
        </div>
      )}

      {status && <div className="text-sm text-slate-600">{status}</div>}
    </div>
  );
}

function Field({ label, value, className }: { label: string; value?: string; className?: string }) {
  return (
    <div className={className}>
      <div className="label">{label}</div>
      <div className={value ? "font-medium" : "text-slate-400"}>{value || "—"}</div>
    </div>
  );
}
